import React, { useRef, useState, useCallback, useEffect } from 'react'
import { AnnotationCanvas } from './AnnotationCanvas'
import { AnnotationToolbar } from './AnnotationToolbar'
import { ScreenshotGallery } from './ScreenshotGallery'
import { useAnnotations, useAnnotationHistory } from '../../hooks/useAnnotations'
import { useClientScreenshots } from '../../hooks/useClientScreenshots'
import { useScreenshot } from './ScreenshotCapture'

interface ClientSideAnnotationSystemProps {
  videoElement?: HTMLVideoElement | null
  staticImageUrl?: string
  width?: number
  height?: number
  readonly?: boolean
  className?: string
  onMemoryWarning?: (usage: { totalMB: number; avgMBPerScreenshot: number }) => void
}

const MEMORY_WARNING_THRESHOLD = 150 // MB

export const ClientSideAnnotationSystem: React.FC<ClientSideAnnotationSystemProps> = ({
  videoElement,
  staticImageUrl,
  width = 800,
  height = 500,
  readonly = false,
  className = '',
  onMemoryWarning
}) => {
  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false)
  const [lastMemoryCheck, setLastMemoryCheck] = useState(0)

  // Annotation state management
  const {
    annotations,
    currentTool,
    selectedAnnotationId,
    toolColor,
    toolSize,
    addAnnotation,
    updateAnnotation,
    deleteAnnotation,
    selectAnnotation,
    setTool,
    setToolColor,
    setToolSize,
    undo,
    redo,
    clear
  } = useAnnotations()

  const { canUndo, canRedo } = useAnnotationHistory({ 
    annotations, 
    currentTool, 
    selectedAnnotationId, 
    toolColor, 
    toolSize,
    history: [],
    historyIndex: 0
  })

  // Client-side screenshot management
  const {
    screenshots,
    selectedScreenshotId,
    isAtLimit,
    currentScreenshot,
    addScreenshot,
    deleteScreenshot,
    selectScreenshot,
    updateScreenshotAnnotations,
    copyScreenshotToClipboard,
    copySelectedScreenshotsToClipboard,
    clearAllScreenshots,
    getMemoryUsage
  } = useClientScreenshots()

  const { takeElementScreenshot, takeVideoFrameScreenshot } = useScreenshot()

  // Memory monitoring
  useEffect(() => {
    const now = Date.now()
    if (now - lastMemoryCheck > 5000) { // Check every 5 seconds
      const usage = getMemoryUsage()
      if (usage.totalMB > MEMORY_WARNING_THRESHOLD) {
        onMemoryWarning?.(usage)
      }
      setLastMemoryCheck(now)
    }
  }, [screenshots.length, getMemoryUsage, onMemoryWarning, lastMemoryCheck])

  // Sync annotations with selected screenshot
  useEffect(() => {
    if (currentScreenshot) {
      // When switching screenshots, update the annotation canvas
      // This is handled by passing the current screenshot's annotations to the canvas
    }
  }, [currentScreenshot])

  // Save annotations to current screenshot whenever they change
  useEffect(() => {
    if (selectedScreenshotId && annotations.length >= 0) {
      updateScreenshotAnnotations(selectedScreenshotId, annotations)
    }
  }, [annotations, selectedScreenshotId, updateScreenshotAnnotations])

  const handleTakeScreenshot = useCallback(async () => {
    if (isCapturingScreenshot || isAtLimit) return
    
    setIsCapturingScreenshot(true)
    
    try {
      let screenshotDataUrl: string

      if (videoElement && !videoElement.paused && !videoElement.ended) {
        // Capture current video frame
        screenshotDataUrl = await takeVideoFrameScreenshot(videoElement)
      } else if (staticImageUrl) {
        // Use provided static image
        screenshotDataUrl = staticImageUrl
      } else if (canvasContainerRef.current) {
        // Capture the current canvas area (fallback)
        screenshotDataUrl = await takeElementScreenshot(canvasContainerRef.current)
      } else {
        throw new Error('No capture source available')
      }

      const success = await addScreenshot(screenshotDataUrl)
      
      if (success) {
        // Clear current annotations since we're starting fresh with a new screenshot
        clear()
        
        // Show success feedback
        console.log('Screenshot captured successfully')
      }

    } catch (error) {
      console.error('Screenshot capture failed:', error)
      alert('Failed to capture screenshot. Please try again.')
    } finally {
      setIsCapturingScreenshot(false)
    }
  }, [
    isCapturingScreenshot, 
    isAtLimit, 
    videoElement, 
    staticImageUrl, 
    takeVideoFrameScreenshot, 
    takeElementScreenshot, 
    addScreenshot, 
    clear
  ])

  const handleScreenshotSelect = useCallback((id: string) => {
    selectScreenshot(id)
    const screenshot = screenshots.find(s => s.id === id)
    if (screenshot) {
      // Load the screenshot's annotations into the canvas
      clear()
      screenshot.annotations.forEach(annotation => {
        addAnnotation(annotation)
      })
    }
  }, [selectScreenshot, screenshots, clear, addAnnotation])

  const handleCopyScreenshot = useCallback(async (id: string): Promise<boolean> => {
    try {
      const success = await copyScreenshotToClipboard(id)
      if (success) {
        // Show success message
        const screenshot = screenshots.find(s => s.id === id)
        const time = screenshot ? new Date(screenshot.createdAt).toLocaleTimeString() : ''
        alert(`Screenshot from ${time} copied to clipboard!`)
      }
      return success
    } catch (error) {
      console.error('Copy failed:', error)
      return false
    }
  }, [copyScreenshotToClipboard, screenshots])

  const handleCopySelectedScreenshots = useCallback(async (selectedIds: string[]): Promise<boolean> => {
    try {
      const success = await copySelectedScreenshotsToClipboard(selectedIds)
      if (success) {
        alert(`${selectedIds.length} screenshot${selectedIds.length > 1 ? 's' : ''} copied to clipboard!`)
      }
      return success
    } catch (error) {
      console.error('Multi-copy failed:', error)
      return false
    }
  }, [copySelectedScreenshotsToClipboard])

  // Get current background image for annotation canvas
  const getCurrentBackgroundImage = useCallback(() => {
    if (currentScreenshot) {
      return currentScreenshot.dataUrl
    }
    return staticImageUrl
  }, [currentScreenshot, staticImageUrl])

  // Get current annotations for display
  const getCurrentAnnotations = useCallback(() => {
    return currentScreenshot ? currentScreenshot.annotations : annotations
  }, [currentScreenshot, annotations])

  const memoryUsage = getMemoryUsage()

  return (
    <div className={`client-annotation-system ${className}`}>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Main Annotation Area - Takes up 2/3 on large screens */}
        <div className="xl:col-span-2">
          <div className="space-y-4">
            {/* Canvas Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {currentScreenshot ? 'Editing Screenshot' : 'Live View'}
              </h3>
              
              <div className="flex items-center space-x-3">
                {/* Screenshot button */}
                <button
                  onClick={handleTakeScreenshot}
                  disabled={isCapturingScreenshot || isAtLimit}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    isAtLimit 
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                      : isCapturingScreenshot
                      ? 'bg-blue-400 text-white cursor-wait'
                      : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  {isCapturingScreenshot ? (
                    <span className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Capturing...
                    </span>
                  ) : isAtLimit ? (
                    '📷 Limit Reached'
                  ) : (
                    '📷 Take Screenshot'
                  )}
                </button>

                {/* Memory usage indicator */}
                <div className={`text-xs px-2 py-1 rounded ${
                  memoryUsage.totalMB > MEMORY_WARNING_THRESHOLD 
                    ? 'bg-red-100 text-red-600' 
                    : memoryUsage.totalMB > 50 
                    ? 'bg-yellow-100 text-yellow-600'
                    : 'bg-green-100 text-green-600'
                }`}>
                  {memoryUsage.totalMB.toFixed(0)}MB
                </div>
              </div>
            </div>

            {/* Annotation Canvas */}
            <div 
              ref={canvasContainerRef}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
              <AnnotationCanvas
                width={width}
                height={height}
                backgroundImage={getCurrentBackgroundImage()}
                annotations={getCurrentAnnotations()}
                currentTool={currentTool}
                toolColor={toolColor}
                toolSize={toolSize}
                onAnnotationAdd={addAnnotation}
                onAnnotationUpdate={updateAnnotation}
                onAnnotationDelete={deleteAnnotation}
                onAnnotationSelect={selectAnnotation}
                selectedAnnotationId={selectedAnnotationId}
                readonly={readonly}
              />

              {/* Current screenshot indicator */}
              {currentScreenshot && (
                <div className="mt-3 p-2 bg-blue-50 rounded-md">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-blue-700">
                      📷 Editing: {new Date(currentScreenshot.createdAt).toLocaleTimeString()}
                    </span>
                    <button
                      onClick={() => selectScreenshot('')}
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      Return to live view
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Annotation Toolbar */}
            {!readonly && (
              <AnnotationToolbar
                currentTool={currentTool}
                toolColor={toolColor}
                toolSize={toolSize}
                onToolChange={setTool}
                onColorChange={setToolColor}
                onSizeChange={setToolSize}
                onUndo={undo}
                onRedo={redo}
                onClear={clear}
                onScreenshot={handleTakeScreenshot}
                canUndo={canUndo}
                canRedo={canRedo}
              />
            )}
          </div>
        </div>

        {/* Screenshot Gallery - Takes up 1/3 on large screens */}
        <div className="xl:col-span-1">
          <ScreenshotGallery
            screenshots={screenshots}
            selectedScreenshotId={selectedScreenshotId}
            onScreenshotSelect={handleScreenshotSelect}
            onScreenshotDelete={deleteScreenshot}
            onCopyScreenshot={handleCopyScreenshot}
            onCopySelected={handleCopySelectedScreenshots}
            onClearAll={clearAllScreenshots}
            maxScreenshots={10}
            memoryUsage={memoryUsage}
          />
        </div>
      </div>

      {/* Performance Tips */}
      {memoryUsage.totalMB > MEMORY_WARNING_THRESHOLD && (
        <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="text-orange-500 text-lg">⚡</div>
            <div>
              <h4 className="font-medium text-orange-800 mb-2">Performance Tips</h4>
              <ul className="text-sm text-orange-700 space-y-1">
                <li>• Delete old screenshots you no longer need</li>
                <li>• Lower video quality to reduce screenshot file sizes</li>
                <li>• Copy important screenshots to external storage</li>
                <li>• Consider refreshing the page if experiencing slowdowns</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}